import { FC } from "react";
import {
  STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE,
  STAGE_1_APPROVE_VALUE,
  STAGE_2_APPROVE_VALUE,
  STAGE_APPROVED_VALUE,
} from "../../../constants/app";
import { useAppSelector } from "../../../store";
import { TCandidateProfileStesps } from "../../../store/onboarding/types";
import Warning from "../../UI/Icons/Warning";

interface StageNotificationBannerProps {
  reopenedStageNumbers: number[];
  getReopenedStageNames: () => string[];
  activeStage: string | null;
  isStageOneReopened: () => boolean;
  isStageTwoReopened: () => boolean;
  isStageThreeReopened: () => boolean;
  reviewStatus?: string | null;
}

const FOOTER_TEXT =
  "Finish setting up and submit your information for review. Please note that between each stage, it will take up to 1 business day for a manual review to be completed by our consultants.";

const getStageName = (num: number): string => {
  if (num === 1) return "Stage 1: Personal Details & Interview";
  if (num === 2) return "Stage 2: Compliance Checks";
  if (num === 3) return "Stage 3: Contract & Statements";
  return `Stage ${num}`;
};

const formatList = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items.at(-1)}`;
};

const StageNotificationBanner: FC<StageNotificationBannerProps> = ({
  reopenedStageNumbers,
  getReopenedStageNames,
  activeStage,
  isStageOneReopened,
  isStageTwoReopened,
  isStageThreeReopened,
}) => {

  const candidateProfileStages = useAppSelector((state) => state.onboarding.candidateProfileSteps);
  const candidateProfile = useAppSelector((state) => state.onboarding.personaldetails);



  const isStageOneApproved = (): boolean => {
    const stageData = candidateProfileStages?.find(
      (stage: TCandidateProfileStesps) => stage.step_number === 1
    );
    return !!(
      stageData &&
      (stageData.action === STAGE_1_APPROVE_VALUE ||
        stageData.action === STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE)
    );
  };

  const isStageTwoApproved = (): boolean => {
    const stageData = candidateProfileStages?.find(
      (stage: TCandidateProfileStesps) => stage.step_number === 2
    );
    return stageData?.action === STAGE_2_APPROVE_VALUE;
  };

  const isStageThreeApproved = (): boolean => {
    const stageData = candidateProfileStages?.find(
      (stage: TCandidateProfileStesps) => stage.step_number === 3
    );
    return stageData?.action === STAGE_APPROVED_VALUE;
  };

  /**
   * Returns the stage number the candidate still needs to complete for the
   * first time (i.e. has never submitted before), or null if none exists.
   * A stage that is in `reopenedStageNumbers` is NOT a first-time stage.
   */
  const getFirstTimeStageNum = (): number | null => {
    const stageNum = activeStage
      ? Number.parseInt(activeStage.replace("stage", ""), 10)
      : Number.NaN;
    if (Number.isNaN(stageNum)) return null;
    return resolveFirstTimeStage(stageNum); 
  };

  const resolveFirstTimeStage = (stageNum: number): number | null => {
    const isReopened = (n: number) => reopenedStageNumbers.includes(n);
    const profileStage = candidateProfile?.current_stage;

    // Candidate has already submitted stage 3 — nothing left to complete first-time
    if (profileStage === 3) return null;

    // Stage 2 done (approved), candidate still needs to complete stage 3 for the first time
    if (profileStage === 2 && isStageTwoApproved() && !isReopened(3)) return 3;

    if (stageNum <= 1 && !isStageOneReopened() && !isStageOneApproved()) {
      return isReopened(1) ? null : 1;
    }
    if (stageNum <= 2 && isStageOneApproved() && !isStageTwoReopened() && !isStageTwoApproved()) {
      // Stage 2 already submitted (current_stage >= 2) — don't treat it as first-time incomplete
      if (profileStage != null && profileStage >= 2) return null;
      return isReopened(2) ? null : 2;
    }
    if (isStageTwoApproved() && !isStageThreeReopened() && !isStageThreeApproved()) {
      return isReopened(3) ? null : 3;
    }
    return null;
  }; 

  const hasReopened = reopenedStageNumbers.length > 0;
  const firstTimeStageNum = getFirstTimeStageNum();

  return (
    <div className="d-flex">
      <div className="bg-teal-light py-10px px-15px radius-8px d-flex gap-6 max-w-789px">
        <Warning />
        <div className="fs-13 fw-400 text-black">
          {hasReopened && (
            <>
              {formatList(getReopenedStageNames())}{" "}
              {reopenedStageNumbers.length > 1 ? "have" : "has"} been reopened.{" "}
              You are now able to edit your details and resubmit{" "}
              {formatList([...reopenedStageNumbers].sort((a, b) => a - b).map((n) => `Stage ${n}`))}.
              <br />
              <br />
            </>
          )}
          {firstTimeStageNum === null ? (
            FOOTER_TEXT
          ) : (
            <>
              You currently need to complete {getStageName(firstTimeStageNum)}.{" "}
              {FOOTER_TEXT}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StageNotificationBanner;